import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom"; // Renamed Li            p: isSmall ? 24 : 0, // Padding for mobile view outside paperk to RouterLink
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";

// Material UI components
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Paper,
  Fade,
  Link, // MUI Link
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Material UI icons
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const { login, loading, error, currentUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md")); // Used for two-column layout

  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const from = location.state?.from || "/";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const errors = {};
    if (!email) errors.email = t("emailRequired") || "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = t("pleaseEnterValidEmail") || "Please enter a valid email";
    if (!password) errors.password = t("passwordRequired") || "Password is required";
    else if (password.length < 6) errors.password = t("passwordMinLength") || "Password must be at least 6 characters";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const success = await login(email, password);
    if (success) navigate(from, { replace: true });
  };

  // The main Box for Login page is styled by App.js's <main className="auth-page">
  // It already provides: display:flex, alignItems:center, justifyContent:center, minHeight:100vh, bgcolor: theme.palette.background.default
  // So, we don't need another full height/width Box here with background gradients.
  // The decorative circles can be kept if desired, but their colors should be theme-aware.

  return (
    <> {/* Using Fragment as the parent Box is handled by App.js main element */}
      {/* Background decorative elements (optional, make them subtle) */}
      <Box
        sx={{
          position: "fixed", // Use fixed to ensure they are behind content if App.js main has its own stacking context
          top: "-10%",
          right: "-5%",
          width: "clamp(300px, 40vw, 500px)",
          height: "clamp(300px, 40vw, 500px)",
          borderRadius: "4%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
          zIndex: -1, // Ensure behind content
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: "-15%",
          left: "-10%",
          width: "clamp(400px, 50vw, 600px)",
          height: "clamp(400px, 50vw, 600px)",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0)} 70%)`,
          zIndex: -1,
        }}
      />

      <LanguageSelector
        variant="icon" // Simpler variant for login page
        sx={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}
      />

      <Container
        maxWidth="md" // Max width for the two-column layout
        sx={{
          display: "flex",
          flexDirection: isSmall ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isSmall ? 0 : theme.spacing(4), // Match register page gap
          p: 0, // Container padding handled by Paper or internal boxes
        }}
      >
        {/* Left side - Brand/Welcome area - Hidden on small screens, shown on medium+ */}
        {!isSmall && (
          <Fade in={true} timeout={1000}>
            <Box
              sx={{
                flex: "1 1 50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                p: theme.spacing(3, 4), // Match register page padding
                textAlign: "left",
                color: theme.palette.text.primary,
              }}
            >
              <img src="/assets/logo.png" alt="Logo" style={{ height: 80, marginBottom: 24 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
                {t("maintenanceReportSystem") || "FleetPilot"}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: 450 }}>
                {t("systemDescription") || "Manage your heavy machinery and daily operations with precision and ease."}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Right side - Login Form */}
        <Box
          sx={{
            flex: "1 1 auto", // Takes available space, but limited by Paper's maxWidth
            width: "100%", // Full width on small screens
            display: "flex",
            justifyContent: "center", // Center the Paper component
            p: isSmall ? theme.spacing(2, 0) : 0, // Match register page padding
          }}
        >
          <Fade in={true} timeout={800}>
            <Paper
              elevation={isMobile ? 0 : 8} // No elevation on mobile, more on desktop
              sx={{
                width: "100%",
                maxWidth: 420, // Match register page width
                borderRadius: theme.shape.borderRadius * 0.5, // Use theme border radius
                overflow: "hidden", // Keep overflow hidden
                // background is theme.palette.background.paper by default
                // boxShadow is handled by elevation or theme.shadows
              }}
            >
              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                {isSmall && ( // Show logo inside paper on small screens
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <img src="/assets/logo.png" alt="Logo" style={{ height: 60, marginBottom: 8 }} />
                    <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>{t("appName", "FleetPilot")}</Typography>
                  </Box>
                )}
                <Typography variant="h5" component="h1" fontWeight={700} sx={{ mb: 0.5, textAlign: isSmall ? 'center' : 'left' }}>
                  {t("welcome")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, textAlign: isSmall ? 'center' : 'left' }}>
                  {t("loginToAccess")}
                </Typography>

                {error && (
                  <Alert variant="outlined" severity="error" sx={{ mb: 2.5 }} icon={<ErrorOutlineIcon fontSize="inherit" />}>
                    {error}
                  </Alert>
                )}
                {location.state?.message && (
                  <Alert variant="outlined" severity="success" sx={{ mb: 2.5 }}>
                    {location.state.message}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    id="email"
                    label={t("email")}
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>),
                    }}
                    variant="outlined" // Theme will style this
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    name="password"
                    label={t("password")}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><LockIcon color="action" /></InputAdornment>),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={togglePasswordVisibility} edge="end" size="small">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined" // Theme will style this
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ textAlign: "right", mb: 2.5 }}>
                    <Link component={RouterLink} to="/forgot-password" variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                      {t("forgotPassword") || "Forgot password?"}
                    </Link>
                  </Box>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained" // Theme will style this
                    size="large"
                    startIcon={loading ? null : <LoginIcon />}
                    disabled={loading}
                    sx={{ py: 1.25, fontWeight: 600 }} // Minor adjustments, theme handles most
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : t("login")}
                  </Button>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={400}>
                      {t("or") || "OR"}
                    </Typography>
                  </Divider>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t("dontHaveAccount") || "Don't have an account?"}{" "}
                    <Link component={RouterLink} to="/register" color="primary.main" sx={{ fontWeight: "bold" }}>
                      {t("register") || "Register"}
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Container>

      <Box sx={{ position: "absolute", bottom: 8, width: "100%", textAlign: "center", zIndex: 1 }}>
        <Typography variant="caption" color={isSmall ? "text.secondary" : alpha(theme.palette.text.primary, 0.7)} sx={{ opacity: 0.8 }}>
          &copy; {new Date().getFullYear()} {t("appName", "FleetPilot")}
        </Typography>
      </Box>
    </>
  );
};

export default Login;
